import { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import googleAnalytics from '../services/googleAnalytics';

/**
 * Hook principal pour Google Analytics
 * Gère l'initialisation et le tracking automatique des pages
 */
export const useGoogleAnalytics = () => {
  const location = useLocation();

  // Initialisation au montage du composant
  useEffect(() => {
    googleAnalytics.initialize();
  }, []);

  // Tracking automatique des changements de page
  useEffect(() => {
    if (googleAnalytics.isTrackingEnabled()) {
      const pageTitle = document.title;
      const pagePath = location.pathname;
      
      googleAnalytics.trackPageView(pagePath, pageTitle);
    }
  }, [location]);

  // Fonctions de tracking
  const trackEvent = useCallback((eventName, parameters) => {
    googleAnalytics.trackEvent(eventName, parameters);
  }, []);

  const trackConversion = useCallback((conversionName, value, currency) => {
    googleAnalytics.trackConversion(conversionName, value, currency);
  }, []);

  const trackUserInteraction = useCallback((action, category, label) => {
    googleAnalytics.trackUserInteraction(action, category, label);
  }, []);

  const trackButtonClick = useCallback((buttonName, location) => {
    googleAnalytics.trackButtonClick(buttonName, location);
  }, []);

  const trackDownload = useCallback((fileName, fileType) => {
    googleAnalytics.trackDownload(fileName, fileType);
  }, []);

  const trackExternalLink = useCallback((url, linkText) => {
    googleAnalytics.trackExternalLink(url, linkText);
  }, []);

  return {
    trackEvent,
    trackConversion,
    trackUserInteraction,
    trackButtonClick,
    trackDownload,
    trackExternalLink,
    isTrackingEnabled: googleAnalytics.isTrackingEnabled()
  };
};

/**
 * Hook pour tracker l'engagement sur une page
 * Mesure le temps passé et les interactions
 */
export const usePageEngagement = (pageName, contentType = 'page') => {
  const startTime = Date.now();

  useEffect(() => {
    let scrollTimeout;
    let hasScrolled = false;

    const handleScroll = () => {
      if (!hasScrolled && window.scrollY > 100) {
        hasScrolled = true;
        googleAnalytics.trackEvent('scroll_engagement', {
          event_category: 'engagement',
          event_action: 'scroll',
          event_label: pageName,
          content_type: contentType
        });
      }
    };

    const handleBeforeUnload = () => {
      const timeSpent = Math.round((Date.now() - startTime) / 1000);
      if (timeSpent > 10) { // Seulement si plus de 10 secondes
        googleAnalytics.trackEvent('page_engagement', {
          event_category: 'engagement',
          event_action: 'time_spent',
          event_label: pageName,
          value: timeSpent,
          content_type: contentType
        });
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      clearTimeout(scrollTimeout);
    };
  }, [pageName, contentType, startTime]);
};

/**
 * Hook pour tracker les performances de page
 * Mesure les Core Web Vitals
 */
export const usePagePerformance = () => {
  useEffect(() => {
    // Attendre que la page soit chargée
    if (document.readyState === 'complete') {
      measurePerformance();
    } else {
      window.addEventListener('load', measurePerformance);
      return () => window.removeEventListener('load', measurePerformance);
    }

    function measurePerformance() {
      // Performance Navigation API
      if ('performance' in window && 'getEntriesByType' in performance) {
        const navigation = performance.getEntriesByType('navigation')[0];
        if (navigation) {
          const loadTime = Math.round(navigation.loadEventEnd - navigation.loadEventStart);
          const domContentLoaded = Math.round(navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart);
          
          googleAnalytics.trackEvent('page_performance', {
            event_category: 'performance',
            event_action: 'page_load',
            page_load_time: loadTime,
            dom_content_loaded: domContentLoaded,
            page_url: window.location.pathname
          });
        }
      }

      // Core Web Vitals (si supporté)
      if ('web-vitals' in window) {
        // Nécessiterait l'installation de web-vitals
        // import { getCLS, getFID, getFCP, getLCP, getTTFB } from 'web-vitals';
      }
    }
  }, []);
};

export default useGoogleAnalytics;
